import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronRightIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
import { compareListings } from '../data/compareListings';
interface ServiceComparePageProps {
  serviceSlug: string;
  onNavigate: (path: string) => void;
}
export function ServiceComparePage({
  serviceSlug,
  onNavigate
}: ServiceComparePageProps) {
  const listingData = compareListings.find(l => l.serviceSlug === serviceSlug);
  if (!listingData) {
    return <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4" data-id="element-2778">
        <h1 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2779">
          Service not found
        </h1>
        <button onClick={() => onNavigate('/')} className="text-amber-600 hover:text-amber-700 font-medium flex items-center gap-2" data-id="element-2780">
          Return Home <ChevronRightIcon className="w-4 h-4" data-id="element-2781" />
        </button>
      </div>;
  }
  const getHeroImage = () => {
    const images: Record<string, string> = {
      'window-replacement': 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=800&q=80',
      roofing: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?auto=format&fit=crop&w=800&q=80',
      'bathroom-remodel': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=800&q=80',
      'kitchen-remodel': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&q=80'
    };
    return images[serviceSlug] || 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=800&q=80';
  };
  return <div className="min-h-screen bg-[#F4F7F6] font-sans pb-16" data-id="element-2782">
      {/* Breadcrumb */}
      <div className="bg-white py-2.5 border-b border-gray-200" data-id="element-2783">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center text-xs font-medium text-gray-500" data-id="element-2784">
          <button onClick={() => onNavigate('/')} className="hover:text-slate-900" data-id="element-2785">
            Home
          </button>
          <span className="mx-2" data-id="element-2786">/</span>
          <button onClick={() => onNavigate(`/near-me/${serviceSlug}`)} className="hover:text-slate-900" data-id="element-2787">
            {listingData.serviceName}
          </button>
          <span className="mx-2" data-id="element-2788">/</span>
          <span className="text-slate-900" data-id="element-2789">Best</span>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-id="element-2790">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-6 mb-8 items-center" data-id="element-2791">
          <div className="flex-1" data-id="element-2792">
            <h1 className="text-3xl md:text-4xl font-serif text-slate-900 mb-3 leading-tight" data-id="element-2793">
              Best {listingData.serviceName} Of 2026
            </h1>
            <p className="text-base text-slate-600 mb-4 leading-relaxed" data-id="element-2794">
              {listingData.description}
            </p>
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500" data-id="element-2795">
              <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-2796">
                <CheckIcon className="w-2.5 h-2.5 text-emerald-600" strokeWidth={3} data-id="element-2797" />
              </div>
              Last Updated Mar 2026
            </div>
          </div>
          <div className="w-full md:w-1/3" data-id="element-2798">
            <img src={getHeroImage()} alt={`${listingData.serviceName} illustration`} className="w-full h-48 object-cover rounded-xl shadow-sm" data-id="element-2799" />
          </div>
        </div>

        {/* Listings */}
        <div className="space-y-4" data-id="element-2800">
          {listingData.companies.map((company, index) => <motion.div key={company.name} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.08
        }} className={`bg-white rounded-xl shadow-sm border overflow-hidden ${company.rank === 1 ? 'border-amber-500 ring-1 ring-amber-500/20' : 'border-slate-200'}`} data-id="element-2801">
              {/* #1 Banner */}
              {company.rank === 1 && <div className="bg-amber-500 text-white text-center py-1.5 font-bold text-xs flex items-center justify-center gap-1.5 tracking-wide" data-id="element-2802">
                  <span data-id="element-2803">🏆</span> #1 Recommended {listingData.serviceName}
                </div>}

              {/* Card Content */}
              <div className="flex flex-col md:flex-row" data-id="element-2804">
                {/* Left: Info & Features */}
                <div className="flex-1 p-4 md:p-5" data-id="element-2805">
                  {/* Company name row */}
                  <div className="flex items-center gap-2.5 mb-3" data-id="element-2806">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-600 font-bold text-sm flex-shrink-0" data-id="element-2807">
                      {company.rank}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 leading-tight" data-id="element-2808">
                      {company.name}
                    </h3>
                  </div>

                  {/* Features in 2 columns */}
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" data-id="element-2809">
                    {company.features.map((feature, idx) => <li key={idx} className="flex items-start gap-1.5 text-[13px] text-slate-600 py-0.5" data-id="element-2810">
                        <CheckIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-2811" />
                        <span className="leading-snug" data-id="element-2812">{feature}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Right: Logo & CTA */}
                <div className="w-full md:w-48 lg:w-56 flex flex-col items-center justify-center gap-3 p-4 md:p-5 md:border-l border-t md:border-t-0 border-slate-100 bg-slate-50/50" data-id="element-2813">
                  {company.logoUrl ? <img src={company.logoUrl} alt={`${company.name} logo`} className="h-12 w-auto object-contain" data-id="element-2814" /> : <span className="font-bold text-base text-slate-700 text-center" data-id="element-2815">
                      {company.name}
                    </span>}

                  <button onClick={() => onNavigate(company.partnerRoute || `/${serviceSlug}/quote-form`)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-5 rounded-lg transition-all text-sm shadow-md shadow-emerald-600/25 hover:shadow-lg hover:shadow-emerald-600/30 hover:scale-[1.02] flex items-center justify-center gap-1.5" data-id="element-2816">
                    See Details
                    <ArrowRightIcon className="w-4 h-4" data-id="element-2817" />
                  </button>

                  <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1" data-id="element-2818">
                    <StarIcon className="w-3 h-3 fill-current" data-id="element-2819" />
                    {company.offer}
                  </span>
                </div>
              </div>

              {/* Bottom CTA bar */}
              <div className="border-t border-slate-100 bg-slate-50 px-4 md:px-5 py-3 flex items-center justify-between gap-4" data-id="element-2820">
                <span className="text-sm font-semibold text-slate-700" data-id="element-2821">
                  Speak with a {company.name} specialist today
                </span>
                <a href="tel:+18555551234" className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2.5 px-6 rounded-lg transition-all text-sm flex items-center gap-1.5 flex-shrink-0 shadow-md shadow-amber-500/25" data-id="element-2822">
                  📞 Call Now
                </a>
              </div>
            </motion.div>)}
        </div>

        {/* Educational Content Section */}
        <div className="mt-16 bg-white rounded-2xl p-8 shadow-sm border border-slate-200" data-id="element-2823">
          <h2 className="text-2xl font-serif text-slate-900 mb-6" data-id="element-2824">
            What to Look For in a {listingData.serviceName} Company
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-2825">
            <div data-id="element-2826">
              <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2" data-id="element-2827">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-600" data-id="element-2828">
                  1
                </div>
                Licensing and Insurance
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2829">
                Always verify that the contractor holds the proper state and
                local licenses for {listingData.serviceName.toLowerCase()}. They
                should also carry both liability insurance and workers'
                compensation to protect you from any accidents or damage during
                the project.
              </p>
            </div>

            <div data-id="element-2830">
              <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2" data-id="element-2831">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-600" data-id="element-2832">
                  2
                </div>
                Warranties and Guarantees
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2833">
                The best companies offer comprehensive warranties that cover
                both the materials and the labor. Look for clear terms on what
                is covered and for how long. A strong warranty is a sign that
                the company stands behind their work.
              </p>
            </div>

            <div data-id="element-2834">
              <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2" data-id="element-2835">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-600" data-id="element-2836">
                  3
                </div>
                Experience and Specialization
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2837">
                Look for contractors who specialize specifically in{' '}
                {listingData.serviceName.toLowerCase()} rather than general
                handymen. Companies with years of specific experience will be
                better equipped to handle unexpected challenges and deliver
                higher quality results.
              </p>
            </div>

            <div data-id="element-2838">
              <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2" data-id="element-2839">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-600" data-id="element-2840">
                  4
                </div>
                Transparent Pricing
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2841">
                Reputable companies will provide a detailed, written estimate
                before any work begins. Be wary of estimates that are
                significantly lower than competitors, as this can sometimes
                indicate subpar materials or hidden fees that will appear later.
              </p>
            </div>
          </div>

          <div className="mt-8 p-5 bg-slate-50 rounded-xl border border-slate-100" data-id="element-2842">
            <h4 className="font-bold text-slate-900 mb-2" data-id="element-2843">
              Pro Tip for Your {listingData.serviceName} Project
            </h4>
            <p className="text-slate-600 text-sm" data-id="element-2844">
              We recommend getting quotes from at least 3 different companies
              before making a decision. This not only helps you find the best
              price, but also gives you a feel for different companies'
              communication styles and professionalism. Use our comparison list
              above to start reaching out to top-rated providers in your area.
            </p>
          </div>
        </div>
      </main>
    </div>;
}